import { useState } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useThemeContext } from "../../theme/ThemeContext";
import { overlayBackdrop } from "../../theme/styles";
import { RADIUS } from "../../theme/tokens";
import { Button, StarMark, FieldLabel, inputStyle } from "../shared";
import { isValidShift, formatShift } from "../../utils/timeUtils";

// -- SCCO Picker (sub-component, local to ShiftSetupModal) --------------------
function SCCOPicker({ sccoList, activeSCCO, onSelect, onAddNew, onClose, isAdmin }) {
  const { SA, GLASS, TYPOGRAPHY } = useThemeContext();
  useEscapeKey(onClose);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newBadge, setNewBadge] = useState("");
  const [saving, setSaving] = useState(false);
  const activeList = sccoList.filter(s => s.active !== false);

  const handleAgentClick = (s) => { onSelect(s); };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    const created = await onAddNew(newName.trim(), newBadge.trim());
    if (created) {
      onSelect(created);
    }
    setSaving(false);
    setAdding(false);
    setNewName("");
    setNewBadge("");
  };

  return (
    <div style={{ ...overlayBackdrop, zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ ...GLASS.modal,
        borderRadius: 16,
        width: "100%", maxWidth: 380, maxHeight: "80vh", display: "flex", flexDirection: "column",
        position: "relative", overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,0.9)" }}>

        {/* Header */}
        <div style={{ padding: "22px 24px 16px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: SA.white, letterSpacing: "-0.01em" }}>
              Who's working?
            </div>
            <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 2 }}>
              Select your name from the SCCO roster
            </div>
          </div>
          {onClose && (
            <Button variant="icon" onClick={onClose}>×</Button>
          )}
        </div>

        {/* Agent list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 0" }}>
          {activeList.length === 0 && !adding && (
            <div style={{ padding: "32px 24px", textAlign: "center", color: SA.silver, fontSize: TYPOGRAPHY.body, lineHeight: 1.7 }}>
              No agents in roster yet.<br />Add the first one below.
            </div>
          )}
          {activeList.map(s => {
            const isActive = activeSCCO && activeSCCO.id === s.id;
            return (
              <Button key={s.id} variant="tile" active={isActive} onClick={() => handleAgentClick(s)}
                style={{ padding: "13px 20px", gap: 14, textAlign: "left" }}>
                {/* Avatar circle */}
                <div style={{ width: 40, height: 40, borderRadius: RADIUS.circle, flexShrink: 0,
                  background: isActive ? SA.accent : SA.darkGray,
                  border: `2px solid ${isActive ? SA.accent : SA.borderGray}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: TYPOGRAPHY.sub, fontWeight: 700, color: isActive ? "#fff" : SA.silver,
                  fontFamily: "'Barlow',sans-serif", transition: "all 0.2s" }}>
                  {s.badge || s.name.slice(0,2).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: TYPOGRAPHY.note, fontWeight: isActive ? 600 : 500,
                    color: isActive ? SA.white : SA.lightSilver, letterSpacing: "-0.01em" }}>
                    {s.name}
                  </div>
                  {s.badge && (
                    <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, marginTop: 1 }}>
                      Badge #{s.badge}
                    </div>
                  )}
                </div>
                {isActive && (
                  <div style={{ fontSize: 16, color: SA.accent }}>✓</div>
                )}
              </Button>
            );
          })}

          {/* Add new agent form — admin only */}
          {isAdmin && adding ? (
            <div style={{ margin: "10px 16px", background: SA.darkGray, borderRadius: 12,
              border: `1px solid ${SA.borderGray}`, padding: "14px 16px" }}>
              <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginBottom: 10, fontWeight: 500 }}>
                New Agent
              </div>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Full name"
                style={{ width: "100%", background: SA.medGray, border: `1px solid ${SA.borderGray}`,
                  borderRadius: RADIUS.sm, color: SA.white, padding: "9px 12px", fontSize: TYPOGRAPHY.note,
                  fontFamily: "'Barlow',sans-serif", outline: "none", marginBottom: 8 }} />
              <input value={newBadge} onChange={e => setNewBadge(e.target.value.toUpperCase().slice(0,6))}
                placeholder="Badge / ID (optional)"
                style={{ width: "100%", background: SA.medGray, border: `1px solid ${SA.borderGray}`,
                  borderRadius: RADIUS.sm, color: SA.white, padding: "9px 12px", fontSize: TYPOGRAPHY.note,
                  fontFamily: "'Barlow',sans-serif", outline: "none", marginBottom: 10 }} />
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant="primary" disabled={!newName.trim() || saving} onClick={handleAdd}
                  style={{ flex: 1, padding: "9px", borderRadius: RADIUS.md, fontSize: TYPOGRAPHY.body }}>
                  {saving ? "Saving…" : "Add & Select"}
                </Button>
                <Button variant="secondary" onClick={() => { setAdding(false); setNewName(""); setNewBadge(""); }}
                  style={{ padding: "9px 14px", borderRadius: RADIUS.md, fontSize: TYPOGRAPHY.body }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : isAdmin ? (
            <Button variant="tile" onClick={() => setAdding(true)}
              style={{ padding: "13px 20px", gap: 14, color: SA.silver, fontSize: TYPOGRAPHY.body,
                borderTop: `1px solid ${SA.borderGray}`, marginTop: 4 }}>
              <div style={{ width: 40, height: 40, borderRadius: RADIUS.circle,
                background: "transparent", border: `1.5px dashed ${SA.borderGray}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, color: SA.borderGray, flexShrink: 0 }}>
                +
              </div>
              <span style={{ fontWeight: 500 }}>Add new agent to roster</span>
            </Button>
          ) : null}
        </div>

        {/* Skip option */}
        {onClose && (
          <div style={{ padding: "12px 20px", borderTop: `1px solid ${SA.borderGray}` }}>
            <Button variant="secondary" fullWidth onClick={onClose}
              style={{ padding: "10px", fontSize: TYPOGRAPHY.caption, fontWeight: 500 }}>
              Skip for now
            </Button>
          </div>
        )}
      </div>

    </div>
  );
}

// -- Shift Setup Modal --------------------------------------------------------
export function ShiftSetupModal({ initialShift, onSave, onClose, isFirstTime, onImportExcel }) {
  const { SA, GLASS, tokens, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(isFirstTime ? null : onClose);
  // Split existing shift into start/end or default empty
  const parts = (initialShift || "").split("-");
  const [startTime, setStartTime] = useState(parts[0] || "");
  const [endTime,   setEndTime]   = useState(parts[1] || "");
  const [error, setError] = useState("");

  const combined = startTime + "-" + endTime;
  const valid = isValidShift(combined);

  const handleSave = () => {
    if (!valid) { setError("Enter valid 24hr times, e.g. 09:30 and 16:00"); return; }
    onSave(combined, isFirstTime);
  };

  const formatTimeInput = (val) => {
    // Allow only digits, strip everything else, cap at 4 digits
    return val.replace(/[^0-9]/g, "").slice(0, 4);
  };

  const timeInputStyle = (val) => ({
    ...inputStyle(false, SA),
    fontSize: 26, letterSpacing: "0.14em", textAlign: "center",
    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
    flex: 1,
    border: `1px solid ${val.length === 4 ? tokens.successBorder : SA.borderGray}`
  });

  return (
    <div style={{ ...overlayBackdrop, zIndex: 400,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ ...GLASS.modal,
        borderRadius: 16, width: "100%", maxWidth: 380, boxShadow: "0 24px 80px rgba(0,0,0,0.9)",
        display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ padding: "18px 24px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <StarMark size={16} color={SA.lightSilver} />
            <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.lightSilver, letterSpacing: "0.2em",
              textTransform: "uppercase", fontFamily: "'Helvetica Neue',Arial,sans-serif", fontWeight: 600 }}>
              {isFirstTime ? "Set Your Shift" : "Edit Shift"}
            </span>
          </div>
          {!isFirstTime && (
            <Button variant="icon" onClick={onClose}
              style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
          <p style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, letterSpacing: "0.04em", marginBottom: 24,
            lineHeight: 1.7, fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
            {isFirstTime
              ? "Enter your scheduled shift window. Connections outside this window will automatically be placed in Post-Shift."
              : "Update your shift window. All entries will be re-evaluated automatically."}
          </p>

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel SA={SA}>Shift Start</FieldLabel>
                <input
                  value={startTime}
                  onChange={e => { setError(""); setStartTime(formatTimeInput(e.target.value)); }}
                  placeholder="0930"
                  maxLength={4}
                  inputMode="numeric"
                  style={timeInputStyle(startTime)}
                />
              </div>
              <div style={{ fontSize: 20, color: SA.borderGray, paddingTop: 18, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>-</div>
              <div style={{ flex: 1 }}>
                <FieldLabel SA={SA}>Shift End</FieldLabel>
                <input
                  value={endTime}
                  onChange={e => { setError(""); setEndTime(formatTimeInput(e.target.value)); }}
                  placeholder="1600"
                  maxLength={4}
                  inputMode="numeric"
                  style={timeInputStyle(endTime)}
                />
              </div>
            </div>
            {error && (
              <div style={{ fontSize: TYPOGRAPHY.tiny, color: tokens.monoRed, letterSpacing: "0.1em", marginTop: 6,
                fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>{error}</div>
            )}
            {valid && !error && (
              <div style={{ fontSize: TYPOGRAPHY.tiny, color: tokens.monoGreen, letterSpacing: "0.1em", marginTop: 6,
                fontFamily: "'Helvetica Neue',Arial,sans-serif" }}>
                {formatShift(combined)}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 24px 16px", borderTop: `1px solid ${SA.borderGray}`,
          display: "flex", flexDirection: "column", gap: 8 }}>
          <Button fullWidth disabled={!valid} onClick={handleSave}
            style={{ borderRadius: 2, padding: "14px", fontSize: TYPOGRAPHY.label,
              letterSpacing: "0.18em", textTransform: "uppercase",
              fontFamily: "'Helvetica Neue',Arial,sans-serif", gap: 10,
              background: valid ? SA.medGray : "transparent",
              border: `1px solid ${valid ? SA.lightSilver : SA.borderGray}`,
              color: valid ? SA.white : SA.borderGray }}>
            <StarMark size={11} color={valid ? SA.lightSilver : SA.borderGray} />
            {isFirstTime ? "Set Shift & Continue" : "Save & Re-evaluate"}
          </Button>

          {isFirstTime && onImportExcel && (
            <Button fullWidth disabled={!valid}
              onClick={() => { if (valid) { onSave(combined, true); } else { onImportExcel(); } }}
              style={{ borderRadius: 2, padding: "12px", fontSize: TYPOGRAPHY.tiny,
                letterSpacing: "0.14em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif", gap: 8,
                background: "transparent",
                border: `1px solid ${SA.borderGray}`,
                color: valid ? SA.silver : SA.borderGray }}>
              ↑ Import Excel After Setting Shift
            </Button>
          )}
          {!isFirstTime && (
            <Button fullWidth onClick={onClose}
              style={{ borderRadius: 2, padding: "14px", fontSize: TYPOGRAPHY.label,
                letterSpacing: "0.18em", textTransform: "uppercase",
                fontFamily: "'Helvetica Neue',Arial,sans-serif", gap: 10,
                background: "transparent", border: `1px solid ${SA.borderGray}`,
                color: SA.silver }}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export { SCCOPicker };
